import { Chip } from '@mui/material';

import {
  type ContactStatus,
  type PaywallState,
} from '@/cam-admin/paywall/Shortlist.fixtures';
import { statusLabel } from '@/cam-admin/paywall/Shortlist.content';
import StatusChipMenu from '@/cam-admin/care-search/StatusChipMenu';

// The one status a provider carries, as a chip. Read-only wherever it appears:
// none of the four states is something a CAM sets — the email going out, Stripe
// confirming and the provider saying no are what move it — so this is a chip and
// never a control. Shared by the shortlists tab and the care search shortlist
// pane so the same provider cannot read two different ways on two screens.

// Each state's chip colour, from the brand ramps the theme exposes to Chip
// (cherry, warmOrange, limeGreen, sunflower — see types/mui-augmentations.d.ts).
// Held here rather than at the call site so the mapping is one thing to change.
//
// Colour follows whether the family can see this provider: green once they can,
// yellow while we are waiting on a payment, nothing where the paywall does not
// apply yet.
export const chipColor = (
  state: PaywallState,
): 'limeGreen' | 'sunflower' | 'warmOrange' | 'default' => {
  if (state === 'paid') return 'limeGreen';
  // Messaged is the waiting state, so it takes the waiting colour. It is also
  // where a provider stays if they never answer, which is most of them.
  if (state === 'messaged') return 'sunflower';
  // A no is not a failure, so it is quiet rather than red — but it is settled,
  // which is why it is not the same nothing as draft.
  if (state === 'declined') return 'warmOrange';
  return 'default';
};

// The same four rungs on a care home list, where a CAM rang round instead of a
// paywall moving anything. Deliberately the same colours read off the same
// question — is this settled, are we waiting, is it a no, has anything happened
// at all — so a CAM switching lists in the selector does not have to relearn the
// column. The words differ because the worlds do: nobody pays for a care home.
export const contactChipColor = (
  status: ContactStatus,
): 'limeGreen' | 'sunflower' | 'warmOrange' | 'default' => {
  if (status === 'Secured') return 'limeGreen';
  if (status === 'Contacted') return 'sunflower';
  if (status === 'Unavailable') return 'warmOrange';
  return 'default';
};

// One chip, two status models, and never both: `contactState` is what a care home
// row carries and `state` is what a dom row carries, so whichever is present is
// the one this row lives by.
//
// A dom row is always read-only: the send, Stripe and the provider's answer are
// what move those four, and a control would claim otherwise. A care home row is
// editable wherever the surface can take the edit — pass `onContactStatus` and
// it renders Care search's own menu, so the same four values are set the same
// way in both places. Without it the chip is flat, which is what the drawer and
// the handover frames want.
const StatusChip = ({
  state,
  contactState,
  isPublished,
  onContactStatus,
  onDismiss,
}: {
  state: PaywallState;
  contactState?: ContactStatus;
  isPublished: boolean;
  /** Care home rows only. Given one, the chip becomes the menu. */
  onContactStatus?: (s: ContactStatus) => void;
  /** Care home rows only, and only where the surface can carry a dismiss. */
  onDismiss?: () => void;
}) =>
  contactState && onContactStatus ? (
    <StatusChipMenu
      status={contactState}
      onStatus={(s) => onContactStatus(s as ContactStatus)}
      onDismiss={onDismiss}
      color={contactChipColor(contactState)}
    />
  ) : contactState ? (
    <Chip
      size='small'
      label={contactState}
      color={contactChipColor(contactState)}
      variant={contactState === 'Not contacted' ? 'outlined' : 'filled'}
    />
  ) : (
    <Chip
      size='small'
      label={statusLabel(state, isPublished)}
      color={chipColor(state)}
      variant={state === 'draft' ? 'outlined' : 'filled'}
    />
  );

StatusChip.displayName = 'StatusChip';
export default StatusChip;
